import React, { useState, useEffect } from "react";
import { errorWithoutThrowInProduction } from "shared/lib/logging/logHelper";
import * as HelpfulIcon from "~/components/atoms/ShareButton/HelpfulButton";
import { QuestionAnswer } from "~/models/questionAnswer";

interface HelpfulButtonProps {
  buttonSize?: "small" | "medium";
  helpfulCount?: number;
  increaseFeedbackHelpfulCallback?: () => void;
  questionAnswerId: QuestionAnswer["id"];
  topicId: QuestionAnswer["topicId"];
}

export function getHelpfulStoreKeyForQa({
  questionAnswerId,
}: {
  questionAnswerId: string;
}): string {
  return `_fb_${questionAnswerId}`;
}

function setHasIncreasedHelpfulToLocalStorage({
  questionAnswerId,
}: {
  questionAnswerId: string;
}) {
  localStorage.setItem(getHelpfulStoreKeyForQa({ questionAnswerId }), "1");
}

function removeHasIncreasedHelpfulFromLocalStorage({
  questionAnswerId,
}: {
  questionAnswerId: string;
}) {
  localStorage.setItem(getHelpfulStoreKeyForQa({ questionAnswerId }), "0");
}

function hasIncreasedHelpful({
  questionAnswerId,
}: {
  questionAnswerId: string;
}) {
  const value = localStorage.getItem(
    getHelpfulStoreKeyForQa({ questionAnswerId })
  );

  if (value && value !== "0") {
    return true;
  }

  return false;
}

const HelpfulButton: React.FC<HelpfulButtonProps> = ({
  buttonSize,
  helpfulCount: initialHelpfulCount,
  increaseFeedbackHelpfulCallback,
  questionAnswerId,
  topicId,
}) => {
  const [hasIncreased, setHasIncreased] = useState<boolean>(false);
  const [helpfulCount, setHelpfulCount] = useState<number | undefined>(
    initialHelpfulCount
  );
  const [currentHasIncreased, setCurrentHasIncreased] =
    useState<boolean>(false);

  useEffect(() => {
    const hasIncreasedHelpfulInLocalStorage = hasIncreasedHelpful({
      questionAnswerId,
    });
    setCurrentHasIncreased(hasIncreasedHelpfulInLocalStorage);
    setHasIncreased(hasIncreasedHelpfulInLocalStorage);
  }, [questionAnswerId]);

  const onHelpfullClick = async () => {
    const currentHelpfulCount = helpfulCount ?? 0;
    if (currentHasIncreased) {
      setCurrentHasIncreased(false);
      removeHasIncreasedHelpfulFromLocalStorage({
        questionAnswerId,
      });
      setHelpfulCount(currentHelpfulCount > 0 ? currentHelpfulCount - 1 : 0);
      return;
    }

    try {
      setCurrentHasIncreased(true);
      setHasIncreasedHelpfulToLocalStorage({
        questionAnswerId,
      });
      setHelpfulCount(currentHelpfulCount + 1);

      // prevent users to actually increase helpful count multiple times by toggling button
      if (!hasIncreased && topicId) {
        setHasIncreased(true);
        increaseFeedbackHelpfulCallback?.();
      }
    } catch (err) {
      errorWithoutThrowInProduction({
        errorInfo: {
          questionAnswerId,
          topicId,
        },
        errorMessage: `Failed to increase helpful for question answer.`,
      });
    }
  };

  return (
    <HelpfulIcon.default
      iconType={currentHasIncreased ? "solid" : "outline"}
      onClick={onHelpfullClick}
      size={buttonSize}
      isAnimated={currentHasIncreased ? true : false}
      {...(helpfulCount !== undefined
        ? {
            label: helpfulCount > 999 ? "999+" : (helpfulCount || 0).toString(),
          }
        : {})}
    />
  );
};

export default HelpfulButton;
